/**
 * ========================================================================
 * CRITICAL: Console overrides MUST be first, before ANY code or imports
 * Ethers.js logs filter errors with console.log("@TODO", error)
 * ========================================================================
 */

// Suppress ethers.js filter expiration errors (harmless noise from HTTP event fallback)
// These happen because testnet RPC nodes have aggressive filter garbage collection
const originalLog = console.log.bind(console);
const originalError = console.error.bind(console);

// Filter console.log (ethers uses this for @TODO errors, not console.error!)
const filteredLog = function (...args: unknown[]) {
  const allArgs = args.map((arg) => String(arg)).join(' ');
  if (
    allArgs.includes('@TODO') &&
    (allArgs.includes('could not coalesce') ||
      allArgs.includes('filter not found') ||
      allArgs.includes('eth_getFilterChanges') ||
      allArgs.includes('UNKNOWN_ERROR'))
  ) {
    return; // Suppress ethers filter errors
  }
  originalLog.apply(console, args);
};

// Also filter console.error for backup suppression
const filteredError = function (...args: unknown[]) {
  const allArgs = args.map((arg) => String(arg)).join(' ');
  if (
    allArgs.includes('@TODO Error') ||
    allArgs.includes('could not coalesce') ||
    allArgs.includes('filter not found') ||
    allArgs.includes('eth_getFilterChanges') ||
    allArgs.includes('UNKNOWN_ERROR') ||
    // Suppress WebSocket connection errors (DNS failures, connection refused, etc.)
    // These are expected when testing environments block WebSockets or DNS fails
    (allArgs.includes('WebSocket connection') &&
      (allArgs.includes('ERR_NAME_NOT_RESOLVED') ||
        allArgs.includes('ERR_CONNECTION_REFUSED') ||
        allArgs.includes('ERR_CONNECTION_TIMED_OUT')))
  ) {
    return; // Suppress
  }
  originalError.apply(console, args);
};

// Replace console.log and console.error with filtered versions
Object.defineProperty(console, 'log', {
  configurable: false,
  enumerable: true,
  writable: false,
  value: filteredLog,
});

Object.defineProperty(console, 'error', {
  configurable: false,
  enumerable: true,
  writable: false,
  value: filteredError,
});

/**
 * main.ts - Application Entry Point
 *
 * Initializes the Vue application with:
 * - Global component registration (FlmButton, FlmCard, FlmTooltip, FlmNumber)
 * - Custom directives (v-click-outside, v-intersect)
 * - Router setup
 * - Console filtering (suppress noisy library warnings)
 * - Cache version management (auto-reload on version mismatch)
 * - Blob storage pre-fetching (warm cache for common blobs)
 *
 * **Console Filtering:**
 * - Ethers.js: Suppresses "@TODO" filter expiration errors (HTTP event fallback noise)
 * - Reown library: Suppresses "Discarding cache for address" messages
 * - WebSocket: Suppresses connection failure warnings (expected in some environments)
 *
 * **Startup Sequence:**
 * 1. Filter console output (synchronous)
 * 2. Import and register components (synchronous)
 * 3. Check cache version (async, may reload page)
 * 4. Pre-fetch common blobs (async, non-blocking)
 * 5. Mount app (async, after cache check)
 */

/**
 * Filter out noisy Reown library console messages
 * Suppresses "Discarding cache for address" messages that clutter logs
 */
const originalConsoleInfo = console.info;
console.info = (...args: unknown[]) => {
  const message = String(args[0] || '');
  if (message.includes('Discarding cache for address')) {
    return;
  }
  originalConsoleInfo.apply(console, args);
};

/**
 * Filter out WebSocket connection warnings
 * These are expected in certain environments (PageSpeed, network issues, DNS problems)
 * The app gracefully falls back to manual polling when WebSocket is unavailable
 */
const originalConsoleWarn = console.warn;
console.warn = (...args: unknown[]) => {
  const message = String(args[0] || '');
  // Suppress WebSocket connection failures (DNS errors, connection establishment failures)
  if (
    (message.includes('WebSocket connection') && message.includes('failed')) ||
    message.includes('ERR_NAME_NOT_RESOLVED') ||
    message.includes('ERR_CONNECTION')
  ) {
    return;
  }
  originalConsoleWarn.apply(console, args);
};

/**
 * ========================================================================
 * Now safe to import modules
 * ========================================================================
 */
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import './css/main.css';
import { checkAndClearCacheIfNeeded } from './common/localDb';
import { logError } from './common/helpers';

// Directives
import vClickOutside from '@/directives/clickOutside.ts';
import { vIntersect } from '@/directives/intersect.ts';

const app = createApp(App);

// Install router
app.use(router);

/**
 * Async initialization sequence
 * 1. Check cache version and reload if needed
 * 2. Pre-fetch common blobs (non-blocking)
 * 3. Mount app
 */
(async () => {
  try {
    // Check if cache needs to be cleared due to version mismatch
    const cacheCleared = await checkAndClearCacheIfNeeded();
    if (cacheCleared) {
      // Reload page to ensure clean state after cache clear
      window.location.reload();
      return;
    }
  } catch (error) {
    logError('main', 'checking cache version', error);
  }

  // Mount app after cache check (directives registered inline)
  app.directive('click-outside', vClickOutside).directive('intersect', vIntersect).mount('#app');
})();
